import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { rememberCurrentPage, getRememberedPage, clearRememberedPage } from '../../lib/navigation';
import Header from '../../archive/Components/layout/Header';
import { Button } from '../../archive/Components/ui/button';
import { Badge } from '../../archive/Components/ui/badge';
import { Input } from '../../archive/Components/ui/input';
import { Check, X, Rocket, Zap, Sparkles, PlusCircle, Lock, Shield, ArrowLeft } from 'lucide-react';
import { Switch } from '../../archive/Components/ui/switch';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../hooks/useAuth';
import { apiFetch } from '../../lib/apiClient';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const plans = {
  free: {
    name: 'Free',
    description: 'For trying out the basics.',
    features: ['1 vault', '3 insights'],
    price: 0,
    icon: Sparkles,
  },
  starter: {
    name: 'Starter',
    price: { monthly: 99, yearly: 990 }, 
    description: 'The Hobbyist. Build your footprint.',
    features: ['4 Episodes/mo', 'Basic Insights', 'Standard Hubs'],
    cta: 'Choose Starter',
    icon: Sparkles,
  },
  pro: {
    name: 'Pro',
    price: { monthly: 199, yearly: 1990 },
    description: 'The Authority. Permanent knowledge engine.',
    features: ['Unlimited Episodes', 'Deep Insights (GPT-4)', 'SEO Hubs', 'Brand Voice'],
    cta: 'Choose Pro',
    popular: true,
    icon: Rocket,
  },
  enterprise: {
    name: 'Enterprise',
    description: 'The Legend. For networks and brands.',
    price: null,
    features: ['Custom Integrations', 'White-label', 'API Access'],
    cta: 'Contact Sales',
    icon: Rocket, // Or Crown if imported
  }
};

const topUpPlan = {
  name: 'Vault Credit',
  price: 19.99,
  description: 'Unlock a single vault with full insights.',
  cta: 'Buy One Credit',
  icon: PlusCircle,
};

const PricingCard = ({ plan, isSelected, onSelect, billingCycle }) => {
  const { name, description, features, nonFeatures = [], cta, popular, price, icon: Icon } = plan;

  return (
    <div
      className={`relative border-2 rounded-2xl p-8 h-full flex flex-col transition-all duration-300 ${isSelected ? 'border-fuchsia-500 shadow-glow-fuchsia-md' : 'border-slate-700 hover:border-slate-500'} ${name === 'Free' ? 'bg-slate-800/50' : 'cursor-pointer'}`}
      onClick={() => {
        if (name !== 'Free') {
          rememberCurrentPage();
          onSelect(name);
        }
      }}
    >
      {popular && (
        <Badge className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-400 to-fuchsia-500 text-white font-semibold border-none">
          Most Popular
        </Badge>
      )}
      <div className="flex-grow">
        <Icon className="w-10 h-10 mb-4 text-emerald-400" />
        <h3 className="text-2xl font-bold text-white">{name}</h3>
        <p className="text-slate-400 mt-2 mb-6">{description}</p>
        {price && typeof price === 'object' ? (
          <div className="my-8">
            <span className="text-5xl font-bold text-white">
              ${billingCycle === 'yearly' ? (price.yearly / 12).toFixed(0) : price.monthly}
            </span>
            <span className="text-slate-400">/month</span>
            {billingCycle === 'yearly' && (
              <p className="text-xs text-slate-400 mt-1">Billed as ${price.yearly.toFixed(2)} per year</p>
            )}
          </div>
        ) : price ? (
          <div className="my-8">
            <span className="text-5xl font-bold text-white">${price.toFixed(2)}</span>
            <span className="text-slate-400">/one-time</span>
          </div>
        ) : null}
        <ul className="space-y-4">
          {features.map((feature, i) => (
            <li key={i} className="flex items-center gap-3">
              <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <span className="text-slate-300">{feature}</span>
            </li>
          ))}
          {nonFeatures.map((feature, i) => (
            <li key={i} className="flex items-center gap-3">
              <X className="w-5 h-5 text-slate-500 flex-shrink-0" />
              <span className="text-slate-400">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      {name !== 'Free' && (
        <Button 
          onClick={() => onSelect(name)}
          variant={isSelected ? 'default' : 'outline'}
          className={`w-full mt-8 font-bold ${isSelected ? 'bg-gradient-to-r from-emerald-500 to-fuchsia-500 text-white border-none' : 'border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white'}`}>
          {cta}
        </Button>
      )}
    </div>
  );
};

const CheckoutForm = ({ planDetails, billingCycle, onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`, // Redirect to the new payment success page for processing
        receipt_email: email,
      },
    });

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      if (onPaymentSuccess) onPaymentSuccess();
      return;
    }

    if (error && (error.type === "card_error" || error.type === "validation_error")) {
      setMessage(error.message);
    } else if (error) {
      setMessage("An unexpected error occurred.");
    }
    setIsProcessing(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="email" className="text-sm font-medium text-slate-300">Email Address</label>
        <div className="relative mt-1">
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            className="pl-4 bg-slate-700/50 border-slate-600 focus:border-emerald-400 focus:ring-emerald-400/20 text-white"
            required
          />
        </div>
      </div>

      <PaymentElement id="payment-element" options={{ layout: 'tabs' }} />

      <Button
        type="submit"
        size="lg"
        className="w-full mt-4 text-lg font-bold h-14 bg-gradient-to-r from-emerald-500 to-fuchsia-500 text-white hover:shadow-glow-fuchsia-lg transition-all duration-300"
        disabled={isProcessing || !stripe || !elements}
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" />
            Processing...
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 mr-3" />
            Pay Securely
          </>
        )}
      </Button>

      {message && <div id="payment-message" className="text-red-400 text-sm mt-2 text-center">{message}</div>}

      <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
        <Shield className="w-4 h-4" />
        Secure payment via Stripe
      </div>
    </form>
  );
};

const UpgradeBanner = ({ onSelectPlan }) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="relative bg-slate-800/50 border border-slate-700 rounded-lg p-8 mb-12 text-center shadow-lg">
      <button 
        onClick={() => setIsVisible(false)} 
        className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-5 h-5" />
      </button>
      <Badge variant="secondary" className="mb-4 bg-gradient-to-r from-fuchsia-600 to-emerald-600 text-white border-none">You are on the Free Plan</Badge>
      <h2 className="text-3xl font-bold text-white mb-2">Unlock Your Full Potential</h2>
      <p className="text-slate-300 max-w-xl mx-auto mb-6">Upgrade to Pro to get unlimited vault creation, advanced remixing, exports, and priority support.</p>
      <div className="flex justify-center gap-4">
        <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold" onClick={() => onSelectPlan('pro')}>
          <Rocket className="w-5 h-5 mr-2" />
          Upgrade to Pro
        </Button>
        <Button size="lg" variant="outline" className="text-white border-slate-600 hover:bg-slate-700 hover:text-white font-bold" onClick={() => onSelectPlan('top-up')}>
          <Zap className="w-5 h-5 mr-2" />
          Buy Credits
        </Button>
      </div>
    </div>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { plan } = useAuth(); // 'free' or 'pro'

  const [selectedPlan, setSelectedPlan] = useState(location.state?.selectedPlan || 'pro');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [clientSecret, setClientSecret] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [discount, setDiscount] = useState(0);
  const [paymentIntentId, setPaymentIntentId] = useState('');

  const handleApplyPromoCode = async () => {
    console.log('Applying promo code with paymentIntentId:', paymentIntentId);

    if (!promoCode) {
      setPromoError('Please enter a promo code.');
      return;
    }
    setPromoError('');

    try {
      const data = await apiFetch('/api/stripe/apply-promo-code', {
        method: 'POST',
        body: JSON.stringify({ 
          promoCode,
          planId: selectedPlan,
          cycle: billingCycle,
          paymentIntentId
        }),
      });

      if (data.error) {
        throw new Error(data.error || 'Failed to apply promo code.');
      }

      // Handle free checkout for Vault Credit
      if (data.free) {
        setDiscount(data.discountAmount);
        setPromoError('');
        // Show a success message (replace with real logic to grant credit)
        alert(data.message || 'Promo code applied! You get this for free.');
        // Optionally, update UI state to reflect credit granted
        return;
      }

      setDiscount(data.discountAmount);
    } catch (error) {
      setPromoError(error.message);
      setDiscount(0);
    }
  };


  useEffect(() => {
    if (selectedPlan) {
      // **ACTION REQUIRED**: Create a backend endpoint to generate a Stripe PaymentIntent.
      // This endpoint should return a `clientSecret`.
      // It needs to know the plan and billing cycle to calculate the amount.
      apiFetch('/api/create-payment-intent', {
        method: 'POST',
        body: JSON.stringify({ planId: selectedPlan, cycle: billingCycle }),
      })
      .then((data) => {
        setClientSecret(data.clientSecret);
        setPaymentIntentId(data.paymentIntentId); // Set paymentIntentId when PaymentIntent is created
      })
      .catch(console.error);
    }
  }, [selectedPlan, billingCycle]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const planDetails = plans[selectedPlan] || topUpPlan;

  const appearance = {
    theme: 'night',
    labels: 'floating',
    variables: {
      colorPrimary: '#10b981',
      colorBackground: '#1e293b',
      colorText: '#d1d5db',
      colorDanger: '#f87171',
      fontFamily: '"Inter", sans-serif',
      borderRadius: '0.5rem',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 font-sans">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {plan === 'free' && <UpgradeBanner onSelectPlan={setSelectedPlan} />}
        
        <div className="relative text-center mb-12">
          <button 
            onClick={handleGoBack} 
            className="absolute left-0 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors p-2 rounded-full hover:bg-slate-700"
            title="Go back"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white tracking-tight">Checkout</h1>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-slate-400">Secure your access.</p>
        </div>

        {/* Selected Plan Summary */}
        <div className="max-w-2xl mx-auto mb-8 p-6 bg-slate-800/50 border border-slate-700 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
             <div>
                <h2 className="text-xl font-bold text-white">Selected Plan: {planDetails.name}</h2>
                <p className="text-slate-400 text-sm">{planDetails.description}</p>
             </div>
             <div className="flex items-center gap-4">
                <div className="text-right">
                     {planDetails.price && typeof planDetails.price === 'object' ? (
                         <div className="text-xl font-bold text-white">${billingCycle === 'yearly' ? (planDetails.price.yearly / 12).toFixed(0) : planDetails.price.monthly}<span className="text-sm font-normal text-slate-400">/mo</span></div>
                     ) : planDetails.price ? (
                         <div className="text-xl font-bold text-white">${planDetails.price}</div>
                     ) : (
                         <div className="text-xl font-bold text-white">Custom</div>
                     )}
                </div>
                <Button variant="ghost" className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-700" onClick={() => navigate('/pricing')}>
                    Change
                </Button>
             </div>
        </div>

        {selectedPlan === 'enterprise' ? (
             <div className="max-w-2xl mx-auto text-center p-12 bg-slate-800/50 border border-slate-700 rounded-2xl">
                 <h3 className="text-2xl font-bold text-white mb-4">You're a Legend.</h3>
                 <p className="text-slate-300 mb-8">
                     Enterprise plans require a custom setup to ensure we meet your scale and integration needs.
                     Please contact our sales team to proceed.
                 </p>
                 <a href="mailto:sales@podvault.ai" className="inline-flex items-center justify-center px-8 py-3 bg-emerald-500 text-white font-bold rounded-lg hover:bg-emerald-400 transition-colors">
                     Contact Sales
                 </a>
             </div>
        ) : (
            <>
                <div className="flex justify-center items-center gap-4 mb-12">
                    <span className={`font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-400'}`}>Monthly</span>
                    <Switch 
                        checked={billingCycle === 'yearly'}
                        onCheckedChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
                        id="billing-cycle"
                    />
                    <span className={`font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-slate-400'}`}>Yearly</span>
                    <Badge variant="outline" className="border-emerald-400/50 text-emerald-400 bg-emerald-900/20">Save ~20%</Badge>
                </div>

                <div className="max-w-2xl mx-auto mt-8 bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold text-white text-center">Finalize Your Purchase</h3>
                    <div className="mt-6 space-y-3 py-4 border-t border-slate-700">
                    <div className="flex justify-between">
                        <span className="text-slate-400">
                        {planDetails.name}
                        {planDetails.name !== 'Vault Credit' && ` (${billingCycle})`}
                        </span>
                        <span className="text-slate-300">
                        $ {typeof planDetails.price === 'object' 
                            ? planDetails.price[billingCycle].toFixed(2) 
                            : planDetails.price?.toFixed(2)}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-slate-400">Discount</span>
                        <span className="text-emerald-400">- $ {discount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-slate-700">
                        <span className="text-lg font-semibold text-white">Total Due</span>
                        <span className="text-2xl font-bold text-white">
                        $ {((
                            typeof planDetails.price === 'object' 
                            ? planDetails.price[billingCycle] 
                            : (planDetails.price || 0)
                            ) - discount).toFixed(2)}
                        </span>
                    </div>
                    </div>

                    <div className="mt-6">
                    <div className="flex gap-2">
                        <Input 
                        type="text" 
                        placeholder="Promo Code" 
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        className="bg-slate-700/50 border-slate-600 focus:border-emerald-400 focus:ring-emerald-400/20 text-white"
                        />
                        <Button 
                        variant="outline" 
                        onClick={handleApplyPromoCode} 
                        className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                        disabled={!paymentIntentId}
                        >
                        Apply
                        </Button>
                    </div>
                    {promoError && <p className="text-red-400 text-sm mt-2">{promoError}</p>}
                    </div>

                    {clientSecret ? (
                    <Elements options={options} stripe={stripePromise}>
                        <CheckoutForm 
                        planDetails={planDetails} 
                        billingCycle={billingCycle} 
                        onPaymentSuccess={() => {
                            const prev = getRememberedPage('/vault');
                            clearRememberedPage();
                            navigate(prev);
                        }}
                        />
                    </Elements>
                    ) : (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-400"></div>
                    </div>
                    )}
                </div>
            </>
        )}
      </main>
    </div>
  );
};

export default Checkout;